import * as React from "react";
import {useEffect} from "react";
import {Button, ButtonGroup, Icon, Label} from "semantic-ui-react";
import {workingConfig} from "../../configChange/ConfigSignal";
import {SftpSyncConfigSelector, SyncConfigWithPath} from "./SftpSyncConfigSelector";

export interface FtpPreviewerProps {

}

export function FtpPreviewer(props: React.PropsWithChildren<FtpPreviewerProps>) {
    const [fileList, setFileList] = React.useState<FileInfo[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<JSX.Element | null>(null);
    const [sourceFolder, setSourceFolder] = React.useState<string | null>(null);
    const [remoteSourceDirChanged, setRemoteSourceDirChanged] = React.useState<boolean>(false);
    const [selectedConfigWithPath, setSelectedConfigWithPath] = React.useState<SyncConfigWithPath | null>(null);

    useEffect(() => {
        console.log("[FtpPreviewer] selectedConfigWithPath changed: ", selectedConfigWithPath);
        if (selectedConfigWithPath) {
            setSourceFolder(selectedConfigWithPath?.config?.remoteSourceDir ?? "");
        }
    }, [selectedConfigWithPath]);

    useEffect(() => {
        console.log("[FtpPreviewer] Source folder changed to: ", sourceFolder);
        setRemoteSourceDirChanged(hasRemoteSourceDirChanged());

        if (sourceFolder != null) {
            fetchFileList().catch(console.error);
        }
    }, [sourceFolder]);


    async function fetchFileList() {
        console.log("[FtpPreviewer] fetchFileList called with sourceFolder: ", sourceFolder);
        if (!selectedConfigWithPath) {
            console.log("[FtpPreviewer] No SFTP config selected, cannot fetch file list.");
            return
        }
        setLoading(true);

        console.log("[FtpPreviewer] Fetching file list for source folder: ", sourceFolder);
        const url = '/fileSystemWorker/readSftpDirectory';

        let config = JSON.parse(JSON.stringify(selectedConfigWithPath?.config)); // deep copy to avoid modifying the original config
        config.remoteSourceDir = sourceFolder ?? config.remoteSourceDir;

        if (config.remoteSourceDir == "") {
            config.remoteSourceDir = null; // null is handled in fileSystemWorker, empty string is not
        }
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                sftpConfig: config,
            })
        })
        if (!response.ok) {

            let body = await response.text();

            setError(<>
                <h3>Error fetching file list:</h3>
                <p>{response.status} : {response.statusText}</p>
                <p>{body}</p>
            </>);
            setFileList([]);
        } else {
            const data = await response.json();
            setFileList(data);
            setError(null);
        }
        setLoading(false);
    }

    function drawFileList() {
        return <table className="ui celled table">
            <thead>
            <tr>
                <th style={{width: "70px"}}>Type</th>
                <th>Name</th>
                <th>Size</th>
                <th>Date</th>
            </tr>
            </thead>
            <tbody>
            {fileList.map((file, index) => (
                <tr key={index}>
                    <td>
                        {file.isDirectory ? displayFolder(file.name) :
                            <Icon color={"teal"} name={"file"}/>}
                    </td>
                    <td>{file.name}</td>
                    <td>{file.size} KB</td>
                    <td>{new Date(file.date).toLocaleString()}</td>

                </tr>
            ))}
            </tbody>
        </table>
    }

    function displayFolder(name: string) {
        return <Icon
            color={"orange"}
            name={"folder"}
            onDoubleClick={() => {
                setSourceFolder(sourceFolder ? sourceFolder + "/" + name : name);
            }}
        />
    }

    function hasRemoteSourceDirChanged() {
        console.log("[FtpPreviewer] Checking if remote source dir has changed. Remote source dir: ", selectedConfigWithPath?.config?.remoteSourceDir);
        let remoteSourceDir = selectedConfigWithPath?.config?.remoteSourceDir ?? "";
        let changed = sourceFolder != remoteSourceDir
        console.log("[FtpPreviewer] Remote source dir changed: ", changed, " (", sourceFolder, " != ", remoteSourceDir, ")");
        return changed
    }

    function overrideRemoteSourceDir() {
        console.log("[FtpPreviewer] Saving remote source dir: ", sourceFolder);
        let sftpConfig = selectedConfigWithPath?.config;
        if (sftpConfig) {
            sftpConfig.remoteSourceDir = sourceFolder;

            let jp = require('jsonpath');
            let path = selectedConfigWithPath?.path;
            let config = JSON.parse(workingConfig.value);
            jp.value(config, path, sftpConfig);
            workingConfig.value = JSON.stringify(config, null, 2);
        }
        setRemoteSourceDirChanged(hasRemoteSourceDirChanged());
        setSelectedConfigWithPath({ ...selectedConfigWithPath }); // Triggert ein Re-Render der Komponente
    }

    return (
        <div>
            <h1>FTP Preview Page</h1>
            <SftpSyncConfigSelector

                onConfigSelected={(configWithPath: SyncConfigWithPath) => {
                    setSelectedConfigWithPath(configWithPath);
                }}
            />
            <Label>{sourceFolder}</Label>
            <br/>
            <ButtonGroup>
                <Button
                    loading={loading}
                    disabled={loading}
                    icon={"reply"}
                    onClick={() => {
                        if (sourceFolder && sourceFolder.length > 0 && sourceFolder.includes("/")) {
                            const parts = sourceFolder.split('/');
                            parts.pop(); // remove the last part
                            setSourceFolder(parts.join('/'));
                        } else {
                            setSourceFolder("");
                        }
                    }}
                />

                {remoteSourceDirChanged && <Button
                    loading={loading}
                    disabled={loading}
                    primary
                    onClick={() => {
                        console.log("[FtpPreviewer] Saving remote source dir: ", sourceFolder);
                        overrideRemoteSourceDir();
                    }}
                >
                    <Icon name={"save"}/>
                    Save this folder as remoteSourceDir
                </Button>
                }


                <Button
                    loading={loading}
                    disabled={loading}
                    icon={"refresh"}
                    onClick={() => {
                        fetchFileList().catch(console.error);
                    }}/>
            </ButtonGroup>
            {error && <div className="ui red message">{error}</div>}
            {fileList.length != 0 && drawFileList()}
        </div>
    );
}


export interface FileInfo {
    name: string,
    size: number,
    date: Date,
    isDirectory: boolean
}
